import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Customer } from '../../types/Customer';
import { fetchCustomers } from '../../api/Customer';
import '../../css/modal.css';

interface Props {
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

const CustomerPickerModal: React.FC<Props> = ({ onClose, onSelect }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    const requestId = ++requestIdRef.current;
    const timeoutId = window.setTimeout(() => {
      (async () => {
        try {
          setLoading(true);
          const data = await fetchCustomers({ search });
          if (mounted && requestId === requestIdRef.current) setCustomers(Array.isArray(data) ? data : []);
        } catch (e) {
          if (mounted && requestId === requestIdRef.current) setCustomers([]);
        } finally {
          if (mounted && requestId === requestIdRef.current) setLoading(false);
        }
      })();
    }, 250);

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      String(c.phoneNumber || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q)
    );
  }, [customers, search]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
        <button className="close-btn" onClick={onClose}>&times;</button>
        <h2>Select Customer</h2>
        <input
          type="text"
          placeholder="Search by name, email, phone, or city"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
          {loading && <div>Loading…</div>}
          {!loading && filtered.length === 0 && <div>No customers found</div>}
          {!loading && filtered.map(c => (
            <div key={c.id} style={{ padding: '8px 6px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                 onClick={() => onSelect(c)}>
              <div style={{ fontWeight: 600 }}>{c.name}</div>
              <div style={{ fontSize: 12, color: '#555' }}>
                {c.email || '-'} · {c.phoneNumber || '-'} · {c.city || '-'}
              </div>
            </div>
          ))}
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="secondary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default CustomerPickerModal;
